import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const NotificationContext = createContext(null);

const POLL_INTERVAL_MS = 20000; // 20s

export const NotificationProvider = ({ children }) => {
  const { user, token, getAuthHeaders, API_URL } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const refresh = useCallback(async () => {
    if (!tokenRef.current) return;
    try {
      const [listRes, countRes] = await Promise.all([
        axios.get(`${API_URL}/api/notifications?limit=30`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/api/notifications/unread-count`, { headers: getAuthHeaders() }),
      ]);
      setNotifications(listRes.data);
      setUnreadCount(countRes.data?.count ?? 0);
    } catch (error) {
      // notifications are non-critical — fail silently
    }
  }, [API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!user) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    refresh();
    const interval = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, refresh]);

  const markRead = useCallback(async (id) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await axios.post(`${API_URL}/api/notifications/${id}/read`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      refresh(); // reconcile on failure
    }
  }, [API_URL, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const markAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnreadCount(0);
    try {
      await axios.post(`${API_URL}/api/notifications/read-all`, {}, { headers: getAuthHeaders() });
    } catch (error) {
      refresh();
    }
  }, [API_URL, refresh]); // eslint-disable-line react-hooks/exhaustive-deps

  const updatePreferences = useCallback(async (prefs) => {
    const res = await axios.put(`${API_URL}/api/notifications/preferences`, prefs, { headers: getAuthHeaders() });
    return res.data?.notification_prefs;
  }, [API_URL]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <NotificationContext.Provider value={{
      notifications,
      unreadCount,
      refresh,
      markRead,
      markAllRead,
      updatePreferences,
    }}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
};
